import { useMemo } from "react";
import SecondaryButton from "@/components/buttons/secondary-button/SecondaryButton";
import InputNumber from "@/components/input-number/InputNumber";
import { literals } from "@/constants/literals";
import { useScanbot } from "@/scanbot/hooks/useScanbot";

interface ScanCodeProps {
  value: string;
  onChange: (val: string) => void;
  codeType: "sapOrder" | "docu9R1";
  label?: string;
  placeholder?: string;
  searchAgainMsg?: boolean;
  noMargingButtom?: boolean;
}

const ScanCode = ({
  value,
  onChange,
  label,
  placeholder,
  codeType,
  searchAgainMsg,
  noMargingButtom,
}: ScanCodeProps) => {
  const { handleSingleScan } = useScanbot();

  const handleOnScan = async () => {
    const _getScan = async () => {
      const result = await handleSingleScan();
      if (result) {
        onChange(codeType === "docu9R1" ? result.substring(0, 10) : result);
      }
      return true;
    };
    if (codeType === "docu9R1") {
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          mutation.addedNodes.forEach((node) => {
            if (!(node instanceof HTMLElement)) return;

            const dialog = node.matches?.(".MuiDialogContent-root")
              ? node
              : node.querySelector?.(".MuiDialogContent-root");

            if (!dialog) return;

            const paragraphs = dialog.querySelectorAll("p.MuiTypography-body1");
            if (!paragraphs.length) return;

            const firstParagraph = paragraphs[0];

            const textObserver = new MutationObserver(() => {
              const text = firstParagraph.textContent ?? "";

              if (text.length > 0) {
                firstParagraph.textContent = text.slice(0, 10);
                textObserver.disconnect();
              }
            });
            textObserver.observe(firstParagraph, {
              characterData: true,
              subtree: true,
              childList: true,
            });
          });
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      await _getScan();

      setTimeout(() => {
        observer.disconnect();
      }, 1000);
      return;
    }
    await _getScan();
  };

  const errorMessage = useMemo(() => {
    return codeType === "sapOrder"
      ? literals.orderNumberFormat
      : literals.r9Format;
  }, [codeType]);

  const showError = useMemo(() => {
    const limit = codeType === "sapOrder" ? 12 : 10;
    return value.length > limit;
  }, [value, codeType]);

  return (
    <div
      className={`w-full ${noMargingButtom ? "mb-0" : "mb-4"} flex flex-row gap-x-1`}
      data-testid="scan-code"
    >
      <InputNumber
        label={label || literals.orderNumber}
        placeholder={placeholder || literals.orderExample}
        requiredAsterisk
        onChange={onChange}
        value={value}
        size="medium"
        autoClean={false}
        error={showError}
        errorMessage={errorMessage}
        warning={searchAgainMsg}
        warningMessage={literals.modificatedAlert}
      />
      <div className="w-[121px] pt-5">
        <SecondaryButton
          colour="red"
          size="medium-l"
          label={literals.scan}
          icon="scanDocument"
          onClick={handleOnScan}
        />
      </div>
    </div>
  );
};

export default ScanCode;
